import { DialogContentText } from '@mui/material';

import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { type DocumentType } from '@/gql';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { FILE_FRAGMENT } from '@/modules/files/fragments/fileFields';
import { getFileDisplayName } from '@/utils/file/fileUtils';

import { MOVE_FILE_MUTATION } from '../content/file-row/options-component/option-items/MoveFileMenuItem';
import {
  removeFileFromCache,
  addNewFileToCache,
} from '../../../utils/fileUpdateFunctions';

export interface ConfirmFileMoveDialogProps {
  rootOwnerId: string;
  rootOwnerName: string;
  file: DocumentType<typeof FILE_FRAGMENT>;
  parent: DocumentType<typeof FILE_FRAGMENT> | null;
  open: boolean;
  onClose: () => void;
}

export const ConfirmFileMoveDialog = ({
  rootOwnerId,
  rootOwnerName,
  file,
  parent,
  open,
  onClose,
}: ConfirmFileMoveDialogProps) => {
  const fileDisplayName = getFileDisplayName(file);

  const [moveFile] = useMutation(MOVE_FILE_MUTATION, {
    onCompleted: onClose,
    update(cache, { data }) {
      const newFile = data?.moveFile?.file;

      removeFileFromCache(cache, file, rootOwnerId);
      addNewFileToCache(cache, newFile, rootOwnerId);
    },
  });

  return (
    <Or2Dialog
      maxWidth="sm"
      title={`Confirm moving ${file.folder ? 'folder' : 'file'}`}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button
            color="primary"
            onClick={() => {
              moveFile({
                variables: {
                  data: {
                    file: file._id,
                    parent: parent?._id ?? null,
                    canMoveOwner: false,
                    owner: null,
                  },
                },
              });
            }}
          >
            Move
          </Or2Button>
        </>
      }
      open={open}
      onClose={onClose}
    >
      <DialogContentText>
        Are you sure you want to move the {file.folder ? 'folder' : 'file'}{' '}
        <strong>{fileDisplayName}</strong> to{' '}
        <strong>{parent?.filename ?? rootOwnerName}</strong>?
      </DialogContentText>
    </Or2Dialog>
  );
};
